import { useState } from "react";
import {
  Box,
  Card,
  TextField,
  Typography,
  InputAdornment,
  Alert,
  styled,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Phone, Email, VpnKey, Lock } from "@mui/icons-material";
import axios from "axios";
import { NavLink } from "react-router-dom";

// ─── Styled Components ───────────────────────────────────────────────────────

const StyledRoot = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
  padding: "16px",
}));

const GlassCard = styled(Card)(() => ({
  width: "100%",
  maxWidth: 440,
  borderRadius: 20,
  padding: "36px 32px",
  background: "rgba(255,255,255,0.04)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
}));

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    background: "rgba(255,255,255,0.05)",
    color: "#fff",
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.35)" },
    "&.Mui-focused fieldset": { borderColor: "#667eea" },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.55)",
    "&.Mui-focused": { color: "#a78bfa" },
  },
  "& .MuiInputAdornment-root .MuiSvgIcon-root": {
    color: "rgba(255,255,255,0.4)",
    fontSize: "1.1rem",
  },
  "& .MuiFormHelperText-root": { color: "#f87171" },
}));

const SubmitButton = styled(LoadingButton)(() => ({
  borderRadius: 12,
  padding: "10px 0",
  fontWeight: 600,
  fontSize: "0.9375rem",
  background: "linear-gradient(90deg, #667eea, #764ba2)",
  boxShadow: "0 4px 20px rgba(102,126,234,0.4)",
  "&:hover": {
    background: "linear-gradient(90deg, #5a6fd6, #6a4190)",
    boxShadow: "0 6px 24px rgba(102,126,234,0.55)",
  },
}));

// ─── Component ───────────────────────────────────────────────────────────────

export default function ForgotPassword() {
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleVerifyOtp = async () => {
    setError("");
    setLoading(true);
    try {
      const result = await axios.post(
        "https://backend.dahiyainsurance.com/VerifyForgotOTP.php",
        { mobile, email, mobileOtp, emailOtp }
      );
      if (result.data.status === 1) {
        setOtpVerified(true);
        setSuccess("OTP verified successfully. Please set a new password.");
      } else {
        setError("Invalid OTPs. Please check and try again.");
      }
    } catch {
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setError("");
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const result = await axios.post(
        "https://backend.dahiyainsurance.com/ResetPassword.php",
        { email, newPassword }
      );
      if (result.data.status === 1) {
        setSuccess("Password reset successfully! You can now log in.");
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledRoot>
      <GlassCard>
        <Typography variant="h5" fontWeight={700} color="#fff" mb={0.5}>
          Reset Password
        </Typography>
        <Typography variant="body2" color="rgba(255,255,255,0.5)" mb={3}>
          Enter your details to verify your identity
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {success}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2.5}>
          <StyledTextField
            fullWidth
            size="small"
            label="Mobile Number"
            value={mobile}
            disabled={otpVerified}
            onChange={(e) => setMobile(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            size="small"
            type="email"
            label="Email Address"
            value={email}
            disabled={otpVerified}
            onChange={(e) => setEmail(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email />
                </InputAdornment>
              ),
            }}
          />

          {!otpVerified && (
            <>
              <StyledTextField
                fullWidth
                size="small"
                label="Mobile OTP"
                value={mobileOtp}
                inputProps={{ maxLength: 6 }}
                onChange={(e) => setMobileOtp(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKey />
                    </InputAdornment>
                  ),
                }}
              />

              <StyledTextField
                fullWidth
                size="small"
                label="Email OTP"
                value={emailOtp}
                inputProps={{ maxLength: 6 }}
                onChange={(e) => setEmailOtp(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKey />
                    </InputAdornment>
                  ),
                }}
              />

              <SubmitButton
                fullWidth
                loading={loading}
                variant="contained"
                onClick={handleVerifyOtp}
              >
                Verify OTP
              </SubmitButton>
            </>
          )}

          {otpVerified && (
            <>
              <StyledTextField
                fullWidth
                size="small"
                type="password"
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                }}
              />

              <StyledTextField
                fullWidth
                size="small"
                type="password"
                label="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                }}
              />

              <SubmitButton
                fullWidth
                loading={loading}
                variant="contained"
                onClick={handleResetPassword}
              >
                Reset Password
              </SubmitButton>
            </>
          )}

          <Typography variant="caption" color="rgba(255,255,255,0.35)" textAlign="center" mt={1}>
            <NavLink to="/login" style={{ color: "#a78bfa", textDecoration: "none" }}>
              ← Back to Login
            </NavLink>
          </Typography>
        </Box>
      </GlassCard>
    </StyledRoot>
  );
}
